import { Link } from '@inertiajs/react'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'
import type { ComponentType, ReactNode } from 'react'

import { cn } from '@/lib/utils'

type QueryValue = string | number | string[] | number[] | null | undefined

type PaginationProps = {
  basePath: string
  currentPage: number
  nextPage: number | null
  prevPage: number | null
  query?: Record<string, QueryValue>
  totalPages: number
}

function buildHref(basePath: string, page: number, query: Record<string, QueryValue> = {}) {
  const search = new URLSearchParams()

  Object.entries(query).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v !== null && v !== undefined && v !== '') {
          search.append(`${key}[]`, String(v))
        }
      })
      return
    }
    search.set(key, String(value))
  })

  search.set('page', String(page))

  return `${basePath}?${search.toString()}`
}

const baseClass =
  'inline-flex h-9 w-9 items-center justify-center rounded-md border border-border text-foreground transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-40'

function IconLink({
  href,
  disabled,
  label,
  Icon,
}: {
  href: string
  disabled: boolean
  label: string
  Icon: ComponentType<{ className?: string }>
}) {
  if (disabled) {
    return (
      <span className={cn(baseClass, 'pointer-events-none opacity-40')} aria-label={label}>
        <Icon className="h-4 w-4" />
      </span>
    )
  }
  return (
    <Link className={baseClass} href={href} preserveScroll aria-label={label}>
      <Icon className="h-4 w-4" />
    </Link>
  )
}

export function Pagination({
  basePath,
  currentPage,
  nextPage,
  prevPage,
  query,
  totalPages,
}: PaginationProps): ReactNode {
  if (totalPages <= 1) return null

  return (
    <nav className="flex items-center justify-end gap-1.5 text-sm">
      <IconLink
        href={buildHref(basePath, 1, query)}
        disabled={!prevPage}
        label="First page"
        Icon={ChevronsLeft}
      />
      <IconLink
        href={buildHref(basePath, prevPage ?? 1, query)}
        disabled={!prevPage}
        label="Previous page"
        Icon={ChevronLeft}
      />
      <span className="px-2 text-muted-foreground tabular-nums">
        {currentPage} / {totalPages}
      </span>
      <IconLink
        href={buildHref(basePath, nextPage ?? totalPages, query)}
        disabled={!nextPage}
        label="Next page"
        Icon={ChevronRight}
      />
      <IconLink
        href={buildHref(basePath, totalPages, query)}
        disabled={!nextPage}
        label="Last page"
        Icon={ChevronsRight}
      />
    </nav>
  )
}
